import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import * as React from 'react';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  answeredCount: number;
  totalQuestions: number;
}

type SubmitStatus = 'idle' | 'loading' | 'error';

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  answeredCount,
  totalQuestions,
}) => {
  const [status, setStatus] = React.useState<SubmitStatus>('idle');

  // Reseta o status toda vez que o dialog abre
  React.useEffect(() => {
    if (open) setStatus('idle');
  }, [open]);

  const handleConfirm = async () => {
    if (status === 'loading') return;
    setStatus('loading');

    try {
      await onConfirm();
      // Sucesso: o pai vai fechar o dialog via `open`
    } catch {
      setStatus('error');
    }
  };

  const handleClose = () => {
    if (status === 'loading') return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px', p: 2 },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: 'Roboto-Bold !important',
          fontSize: '14pt !important',
          color: '#374151',
          textAlign: 'center',
          pb: 1,
        }}
      >
        CONFIRM SUBMISSION
      </DialogTitle>

      <DialogContent>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: 'Roboto-Regular !important',
              fontSize: '11pt !important',
              color: '#6b7280',
              mb: 2,
            }}
          >
            You answered <strong>{answeredCount}</strong> out of{' '}
            <strong>{totalQuestions}</strong> questions.
          </Typography>

          {answeredCount < totalQuestions && (
            <Alert
              severity="warning"
              sx={{ mt: 2, borderRadius: '8px', fontSize: '11pt' }}
            >
              Algumas questões não foram respondidas
            </Alert>
          )}

          {status === 'error' && (
            <Alert
              severity="error"
              sx={{ mt: 2, borderRadius: '8px', fontSize: '11pt' }}
            >
              Falha ao enviar. Verifique sua conexão e tente novamente.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'center',
          pb: 2,
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={status === 'loading'}
          sx={{
            width: { xs: '100%', sm: '10rem' },
            borderColor: '#6b7280',
            color: '#6b7280',
            fontFamily: 'Roboto-Medium !important',
            fontSize: '10pt !important',
            textTransform: 'uppercase',
            borderRadius: '10px',
            px: 3,
            '&:hover': {
              borderColor: '#374151',
              background: 'rgba(107, 114, 128, 0.1)',
            },
            '&.Mui-disabled': { borderColor: '#d1d5db', color: '#9ca3af' },
          }}
        >
          CANCEL
        </Button>

        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={status === 'loading'}
          sx={{
            width: { xs: '100%', sm: '10rem' },
            background:
              'linear-gradient(135deg, #10b981 0%, #059669 100%) !important',
            color: '#ffffff',
            fontFamily: 'Roboto-Bold !important',
            fontSize: '10pt !important',
            textTransform: 'uppercase',
            borderRadius: '10px',
            px: 3,
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            '&:hover': {
              background:
                'linear-gradient(135deg, #059669 0%, #047857 100%) !important',
              boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
            },
            '&.Mui-disabled': {
              background: '#9ca3af !important',
              color: '#ffffff !important',
              boxShadow: 'none !important',
            },
          }}
        >
          {status === 'loading' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: '#ffffff' }} />
              <span>Enviando...</span>
            </Box>
          ) : status === 'error' ? (
            'Tentar novamente'
          ) : (
            'Submit'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// import {
//   Alert,
//   Box,
//   Button,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   Typography,
// } from '@mui/material';
// import * as React from 'react';

// interface ConfirmationDialogProps {
//   open: boolean;
//   onClose: () => void;
//   onConfirm: () => void;
//   answeredCount: number;
//   totalQuestions: number;
// }

// export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
//   open,
//   onClose,
//   onConfirm,
//   answeredCount,
//   totalQuestions,
// }) => {
//   const [isSubmitting, setIsSubmitting] = React.useState(false);

//   const handleConfirm = () => {
//     if (isSubmitting) return;

//     setIsSubmitting(true);
//     onConfirm();
//   };

//   return (
//     <Dialog
//       open={open}
//       onClose={isSubmitting ? undefined : onClose}
//       maxWidth="sm"
//       fullWidth
//       PaperProps={{
//         sx: {
//           borderRadius: '16px',
//           p: 2,
//         },
//       }}
//     >
//       <DialogTitle
//         sx={{
//           fontFamily: 'Roboto-Bold !important',
//           fontSize: '14pt !important',
//           color: '#374151',
//           textAlign: 'center',
//           pb: 1,
//         }}
//       >
//         CONFIRMAR ENVIO
//       </DialogTitle>
//       <DialogContent>
//         <Box sx={{ textAlign: 'center' }}>
//           <Typography
//             sx={{
//               fontFamily: 'Roboto-Regular !important',
//               fontSize: '11pt !important',
//               color: '#6b7280',
//               mb: 2,
//             }}
//           >
//             Você respondeu <strong>{answeredCount}</strong> de{' '}
//             <strong>{totalQuestions}</strong> questões.
//           </Typography>
//           {answeredCount < totalQuestions && (
//             <Alert
//               severity="warning"
//               sx={{ mt: 2, borderRadius: '8px', fontSize: '11pt' }}
//             >
//               Algumas questões não foram respondidas
//             </Alert>
//           )}
//         </Box>
//       </DialogContent>
//       <DialogActions
//         sx={{
//           justifyContent: 'center',
//           pb: 2,
//           gap: 2,
//           flexDirection: { xs: 'column', sm: 'row' },
//         }}
//       >
//         <Button
//           onClick={onClose}
//           variant="outlined"
//           disabled={isSubmitting}
//           sx={{
//             width: { xs: '100%', sm: '10rem' },
//             borderColor: '#6b7280',
//             color: '#6b7280',
//             fontFamily: 'Roboto-Medium !important',
//             fontSize: '10pt !important',
//             textTransform: 'uppercase',
//             borderRadius: '10px',
//             px: 3,
//             '&:hover': {
//               borderColor: '#374151',
//               background: 'rgba(107, 114, 128, 0.1)',
//             },
//             '&.Mui-disabled': {
//               borderColor: '#d1d5db',
//               color: '#9ca3af',
//             },
//           }}
//         >
//           Cancelar
//         </Button>
//         <Button
//           onClick={handleConfirm}
//           variant="contained"
//           // disabled={isSubmitting}
//           sx={{
//             width: { xs: '100%', sm: '10rem' },
//             background:
//               'linear-gradient(135deg, #10b981 0%, #059669 100%) !important',
//             color: '#ffffff',
//             fontFamily: 'Roboto-Bold !important',
//             fontSize: '10pt !important',
//             textTransform: 'uppercase',
//             borderRadius: '10px',
//             px: 3,
//             boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
//             '&:hover': {
//               background:
//                 'linear-gradient(135deg, #059669 0%, #047857 100%) !important',
//               boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
//             },
//             '&.Mui-disabled': {
//               background: '#9ca3af !important',
//               color: '#ffffff !important',
//               boxShadow: 'none !important',
//             },
//           }}
//         >
//           {/* {isSubmitting ? (
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//               <CircularProgress size={20} sx={{ color: '#ffffff' }} />
//               <span>Enviando...</span>
//             </Box>
//           ) : (
//             'Enviar'
//           )} */}
//           Enviar
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };
